import React from 'react';
import LoginForm from './components/LoginForm';

const LoginPage = () => {
  return (
    <div className='h-screen flex overflow-hidden bg-gradient-to-br from-rose-50 via-orange-50/50 to-amber-50/30'>
      {/* Left Panel - Orange Card with Illustration */}
      <div className='hidden lg:flex lg:w-[50%] items-stretch p-4 relative'>
        {/* Animated Decorative Blobs */}
        <div className='absolute bottom-8 left-0 w-24 h-36 bg-gradient-to-t from-orange-400 to-amber-300 rounded-full blur-sm opacity-80 z-20 animate-pulse' />
        <div className='absolute bottom-6 left-16 w-12 h-12 bg-gradient-to-br from-orange-300 to-amber-400 rounded-full z-20 animate-bounce' style={{animationDuration: '3s'}} />
        <div className='absolute bottom-2 left-6 w-6 h-6 bg-gradient-to-br from-amber-300 to-orange-400 rounded-full z-20 animate-pulse' style={{animationDuration: '2s'}} />
        
        {/* Orange Card - Full Height with Enhanced Glassmorphism */}
        <div className='relative z-10 w-full bg-gradient-to-br from-orange-500 via-orange-400 to-amber-400 rounded-3xl p-8 shadow-2xl flex flex-col border border-white/20 backdrop-blur-sm'>
          {/* Floating Particles Effect */}
          <div className='absolute top-4 right-4 w-2 h-2 bg-white/40 rounded-full animate-ping' />
          <div className='absolute top-12 right-8 w-1.5 h-1.5 bg-amber-200/60 rounded-full animate-pulse' />
          <div className='absolute top-20 right-4 w-1 h-1 bg-white/30 rounded-full animate-bounce' style={{animationDuration: '2s'}} />
          
          {/* Headline with Text Shadow */}
          <h1 className='text-3xl xl:text-4xl font-extrabold text-white leading-tight mb-3 italic drop-shadow-lg'>
            Streamline tasks.
            <br />
            <span className='text-orange-100'>Boost employee productivity.</span>
          </h1>
          
          {/* Supporting Text */}
          <p className='text-white/90 text-base mb-6 font-medium'>
            Organize, assign, and track tasks with ease.
          </p>
          
          {/* Illustration Container with Hover Effect */}
          <div className='bg-white rounded-2xl overflow-hidden shadow-lg flex-1 flex items-center justify-center transition-transform duration-500 hover:scale-[1.02] hover:shadow-2xl group'>
            <img 
              src='/kanban-illustration.png' 
              alt='Team collaborating at Kanban board'
              className='w-full h-full object-contain p-2 transition-transform duration-500 group-hover:scale-105'
            />
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className='w-full lg:w-[50%] flex flex-col justify-center items-center px-6 lg:px-10 py-6 relative'>
        {/* Animated Decorative Blobs */}
        <div className='absolute bottom-16 right-0 w-20 h-32 bg-gradient-to-t from-orange-400 to-amber-300 rounded-full blur-sm opacity-70 animate-pulse' />
        <div className='absolute bottom-12 right-12 w-10 h-10 bg-gradient-to-br from-orange-300 to-amber-400 rounded-full opacity-80 animate-bounce' style={{animationDuration: '4s'}} />
        <div className='absolute bottom-6 right-6 w-5 h-5 bg-gradient-to-br from-amber-300 to-orange-400 rounded-full opacity-60 animate-pulse' style={{animationDuration: '2.5s'}} />
        <div className='absolute bottom-24 right-3 w-3 h-3 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full opacity-50 animate-ping' style={{animationDuration: '3s'}} />
        
        <div className='w-full max-w-sm relative z-10'>
          {/* Login Card with Enhanced Shadow and Border */}
          <div className='bg-white/95 backdrop-blur-md rounded-2xl shadow-xl shadow-slate-200/50 p-6 border border-slate-100/80 transition-all duration-300 hover:shadow-2xl hover:shadow-orange-100/50'>
            {/* Logo - Same as Dashboard Header */}
            <div className='flex items-center justify-center gap-3 mb-6'>
              <div className='relative w-11 h-11 flex items-center justify-center'>
                {/* Outer Glow */}
                <div className="absolute inset-0 bg-primary/20 blur-lg rounded-full animate-pulse"></div>
                
                {/* Custom Agno WorkSphere Icon */}
                <svg viewBox="0 0 100 100" className="w-9 h-9 relative z-10 drop-shadow-2xl">
                  <defs>
                    <linearGradient id="login-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#f97316" />
                      <stop offset="100%" stopColor="#fb923c" />
                    </linearGradient>
                  </defs>
                  
                  {/* Orbital Paths */}
                  <circle cx="50" cy="50" r="45" fill="none" stroke="url(#login-grad)" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="10 5" className="animate-[spin_20s_linear_infinite]" />
                  <circle cx="50" cy="50" r="45" fill="none" stroke="url(#login-grad)" strokeWidth="2" strokeDasharray="20 120" className="animate-[spin_10s_linear_infinite]" />
                  
                  {/* Tech Nodes */}
                  <circle cx="50" cy="5" r="3" fill="#f97316" />
                  <circle cx="95" cy="50" r="3" fill="#fb923c" />
                  
                  {/* Central Sphere Core */}
                  <circle cx="50" cy="50" r="22" fill="url(#login-grad)" className="shadow-inner" />
                  
                  {/* Stylized 'A' Core */}
                  <path d="M42 62 L50 38 L58 62 M45 54 L55 54" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className='text-xl font-black text-slate-800 tracking-tight'>
                Agno <span className="text-primary">WorkSphere</span>
              </span>
            </div>

            {/* Welcome Text with Animated Emoji */}
            <div className='text-center mb-5'>
              <h2 className='text-xl font-bold text-slate-800 mb-1'>
                Welcome back! <span className='inline-block animate-bounce'>👋</span>
              </h2>
              <p className='text-slate-500 text-sm'>Let's get things done efficiently</p>
            </div>

            {/* Login Form */}
            <LoginForm />
          </div>

          {/* Footer */}
          <div className='mt-4 text-center'>
            <p className='text-xs text-slate-400'>
              © {new Date().getFullYear()} Agno WorkSphere. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
